import { useEffect, useRef } from "preact/hooks";

type ConfettiParticle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
  shape: "rect" | "circle";
};

const CONFETTI_COLORS = [
  "#FFD700", // Gold
  "#FF6B6B", // Coral
  "#4ECDC4", // Teal
  "#FF69B4", // Pink
  "#7B68EE", // Purple
  "#00CED1", // Cyan
  "#FFA500", // Orange
  "#98FB98", // Pale green
];

function createParticle(canvasWidth: number): ConfettiParticle {
  return {
    x: Math.random() * canvasWidth,
    y: -20,
    vx: (Math.random() - 0.5) * 4,
    vy: Math.random() * 3 + 2,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 10,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]!,
    size: Math.random() * 8 + 4,
    shape: Math.random() > 0.5 ? "rect" : "circle",
  };
}

type ConfettiProps = {
  /** Duration in milliseconds. Default 3000 */
  duration?: number;
  /** Number of particles. Default 100 */
  particleCount?: number;
  /** Class name for the canvas container */
  class?: string;
};

export function Confetti({
  duration = 3000,
  particleCount = 100,
  class: className,
}: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const particlesRef = useRef<ConfettiParticle[]>([]);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const updateSize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);

    // Create initial particles
    particlesRef.current = Array.from({ length: particleCount }, () =>
      createParticle(canvas.width),
    );
    startTimeRef.current = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Stop spawning new particles after duration, but let existing ones fall
      const spawningActive = elapsed < duration;

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter((p) => {
        // Update physics
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1; // Gravity
        p.rotation += p.rotationSpeed;
        p.vx *= 0.99; // Air resistance

        // Remove if off screen
        if (p.y > canvas.height + 50) {
          if (spawningActive) {
            // Respawn at top
            Object.assign(p, createParticle(canvas.width));
          }
          return spawningActive;
        }

        // Draw particle
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
        return true;
      });

      // Continue animation if particles remain
      if (particlesRef.current.length > 0) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", updateSize);
    };
  }, [duration, particleCount]);

  return (
    <canvas
      ref={canvasRef}
      class={`pointer-events-none ${className ?? ""}`}
      style={{ position: "absolute", inset: 0 }}
    />
  );
}
